import { I18nCtx } from 'components/i18n/i18n-context';
import { useState, useCallback, useEffect, useContext } from 'react';

import { Notification } from 'types';

import {
  getNotificationsCount,
  readNotification,
  unreadNotification,
  getNotifications,
} from './notifications-api';
import { getNotificationLanguageFromSelectedLocale } from './notifications-utils';

const INTERVAL = 1000 * 60;

// const unreadFilters = [
//   {
//     field: 'read',
//     operation: 'EQ',
//     value: 'false',
//   },
// ];

async function handleReads(
  ids: string[],
  endpoint: (id: string) => Promise<boolean>,
  callback: () => Promise<void>
) {
  const promises = ids.map((id) => endpoint(id));
  const results = await Promise.all(promises);
  const result = results.every((r) => r);
  if (result) {
    await callback();
  }
  return result;
}

export const useNotifications = () => {
  const [count, setCount] = useState<number>(0);
  const [notifications, setUnreadNotifications] = useState<Notification[]>([]);
  const { locale } = useContext(I18nCtx);

  const getCount = useCallback(async () => {
    const countResult = await getNotificationsCount();
    setCount(countResult);
  }, [setCount]);

  const readNotifications = useCallback(
    async (ids: string[]) => {
      return await handleReads(ids, readNotification, getCount);
    },
    [getCount]
  );

  const unreadNotifications = useCallback(
    async (ids: string[]) => {
      return await handleReads(ids, unreadNotification, getCount);
    },
    [getCount]
  );

  useEffect(() => {
    getCount();
    const interval = setInterval(getCount, INTERVAL);
    return () => {
      clearInterval(interval);
    };
  }, [getCount]);

  useEffect(() => {
    const handleUnreadNotifications = async () => {
      const { items: notifications } = await getNotifications(
        getNotificationLanguageFromSelectedLocale(locale),
        []
      );
      setUnreadNotifications(notifications as Notification[]);
    };

    handleUnreadNotifications();
  }, [count, setUnreadNotifications, locale]);

  return {
    count,
    notifications,
    readNotifications,
    unreadNotifications,
  };
};
